import { sessionRequests } from '@/lib/auth/sessionRequests';
import { DEFAULT_GAME_AD_CONFIG, parseGameAdConfig } from './config';
import type { GameAdConfig } from '../types';
import type { Json } from '@/types/database';

export type GameAdsEventReportRow = {
  experimentId: string;
  configVersion: number;
  variant: string;
  gameMode: 'daily' | 'endless';
  format: string;
  placement: string;
  eventType: string;
  reason: string | null;
  eventCount: number;
  uniqueSessions: number;
  uniquePlayers: number;
  uniqueOpportunities: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  avgElapsedMs: number | null;
  p95ElapsedMs: number | null;
};

export type GameAdsRetentionReportRow = {
  experimentId: string;
  configVersion: number;
  variant: string;
  cohortDate: string;
  cohortPlayers: number;
  returnedDay1: number | null;
  returnedDay7: number | null;
  day1Mature?: boolean;
  day7Mature?: boolean;
};

export type GameAdsAdminReport = {
  windowDays: number;
  generatedAt: string;
  evidence: string;
  cohorts: GameAdsEventReportRow[];
  retention: GameAdsRetentionReportRow[];
  identityBasis?: string | null;
  identityCoverage?: string | null;
  missingIdentityEventsCount?: number | null;
  retentionDayBoundary?: string | null;
  collectionStartedAt?: string | null;
};

export class GameAdsConfigStaleError extends Error {
  constructor() {
    super('Game ads settings changed in another admin session. The latest settings were reloaded.');
    this.name = 'GameAdsConfigStaleError';
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function requireCount(value: unknown, field: string): number {
  const count = value;
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
    throw new Error(`Invalid game ads report: ${field} must be a non-negative integer.`);
  }
  return count;
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Invalid game ads report: ${field} must be a non-empty string.`);
  }
  return value;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function nullableReason(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== 'string') {
    throw new Error('Invalid game ads report: reason must be a string or null.');
  }
  return value.length > 0 ? value : null;
}

function nullableCount(value: unknown, field: string): number | null {
  return value === null || value === undefined ? null : requireCount(value, field);
}

function parseEventRow(value: unknown): GameAdsEventReportRow {
  const row = asRecord(value);
  if (!row) throw new Error('Invalid game ads report: event row must be an object.');
  if (!('reason' in row)) {
    throw new Error('Invalid game ads report: event row is missing reason.');
  }
  if (row.gameMode !== 'daily' && row.gameMode !== 'endless') {
    throw new Error('Invalid game ads report: gameMode must be daily or endless.');
  }
  return {
    experimentId: requireString(row.experimentId, 'experimentId'),
    configVersion: requireCount(row.configVersion, 'configVersion'),
    variant: requireString(row.variant, 'variant'),
    gameMode: row.gameMode,
    format: requireString(row.format, 'format'),
    placement: requireString(row.placement, 'placement'),
    eventType: requireString(row.eventType, 'eventType'),
    reason: nullableReason(row.reason),
    eventCount: requireCount(row.eventCount, 'eventCount'),
    uniqueSessions: requireCount(row.uniqueSessions, 'uniqueSessions'),
    uniquePlayers: requireCount(row.uniquePlayers, 'uniquePlayers'),
    uniqueOpportunities: requireCount(row.uniqueOpportunities, 'uniqueOpportunities'),
    firstSeenAt: nullableString(row.firstSeenAt),
    lastSeenAt: nullableString(row.lastSeenAt),
    avgElapsedMs: nullableCount(row.avgElapsedMs, 'avgElapsedMs'),
    p95ElapsedMs: nullableCount(row.p95ElapsedMs, 'p95ElapsedMs'),
  };
}

function parseRetentionRow(value: unknown): GameAdsRetentionReportRow {
  const row = asRecord(value);
  if (!row) throw new Error('Invalid game ads report: retention row must be an object.');
  return {
    experimentId: requireString(row.experimentId, 'experimentId'),
    configVersion: requireCount(row.configVersion, 'configVersion'),
    variant: requireString(row.variant, 'variant'),
    cohortDate: requireString(row.cohortDate, 'cohortDate'),
    cohortPlayers: requireCount(row.cohortPlayers, 'cohortPlayers'),
    returnedDay1: nullableCount(row.returnedDay1, 'returnedDay1'),
    returnedDay7: nullableCount(row.returnedDay7, 'returnedDay7'),
    ...(typeof row.day1Mature === 'boolean' && { day1Mature: row.day1Mature }),
    ...(typeof row.day7Mature === 'boolean' && { day7Mature: row.day7Mature }),
  };
}

function parseReport(value: unknown): GameAdsAdminReport {
  const report = asRecord(value);
  if (!report || !Array.isArray(report.cohorts) || !Array.isArray(report.retention)) {
    throw new Error('Invalid game ads report response.');
  }
  return {
    windowDays: requireCount(report.windowDays, 'windowDays'),
    generatedAt: requireString(report.generatedAt, 'generatedAt'),
    evidence: requireString(report.evidence, 'evidence'),
    cohorts: report.cohorts.map(parseEventRow),
    retention: report.retention.map(parseRetentionRow),
    identityBasis: nullableString(report.identityBasis),
    identityCoverage: nullableString(report.identityCoverage),
    missingIdentityEventsCount: nullableCount(
      report.missingIdentityEventsCount,
      'missingIdentityEventsCount'
    ),
    retentionDayBoundary: nullableString(report.retentionDayBoundary),
    collectionStartedAt: nullableString(report.collectionStartedAt),
  };
}

export async function adminGetGameAdsReport(days = 30): Promise<GameAdsAdminReport> {
  if (!Number.isInteger(days) || days < 1 || days > 90) {
    throw new Error('Game ads report window must be between 1 and 90 days.');
  }
  const assertOwner = sessionRequests.captureOwner();
  const { supabase } = await import('@/lib/supabase');
  assertOwner();
  const { data, error } = await supabase.rpc('admin_get_game_ad_report', { p_days: days });
  if (error) throw error;
  return parseReport(data);
}

export async function adminSetGameAdsConfig(config: GameAdConfig): Promise<GameAdConfig> {
  if (config.mode !== 'test') {
    throw new Error('Game ads can only be configured in test mode.');
  }
  const p_config: Json = {
    version: config.version,
    aggression: config.aggression,
    rolloutPercent: config.rolloutPercent,
    experimentId: config.experimentId,
    mode: config.mode,
    rewardedEnabled: config.rewardedEnabled,
    interstitialEnabled: config.interstitialEnabled,
    bannerEnabled: config.bannerEnabled,
    interstitialQuestionInterval: config.interstitialQuestionInterval,
    interstitialCooldownSeconds: config.interstitialCooldownSeconds,
    maxInterstitialsPerSession: config.maxInterstitialsPerSession,
    maxRewardedPerSession: config.maxRewardedPerSession,
  };
  const assertOwner = sessionRequests.captureOwner();
  const { supabase } = await import('@/lib/supabase');
  assertOwner();
  const { data, error } = await supabase.rpc('admin_set_game_ad_config', { p_config });
  if (error) {
    if (error.message.includes('GAME_AD_CONFIG_STALE')) throw new GameAdsConfigStaleError();
    throw error;
  }
  const source = asRecord(data);
  const parsed = parseGameAdConfig(data);
  const configFields = Object.keys(DEFAULT_GAME_AD_CONFIG) as Array<keyof GameAdConfig>;
  if (
    !source ||
    configFields.some((field) => !(field in source) || !Object.is(source[field], parsed[field]))
  ) {
    throw new Error('Invalid game ads configuration response.');
  }
  return parsed;
}

const CSV_COLUMNS = [
  'recordType',
  'windowDays',
  'generatedAt',
  'collectionStartedAt',
  'evidence',
  'identityBasis',
  'identityCoverage',
  'missingIdentityEventsCount',
  'retentionDayBoundary',
  'experimentId',
  'configVersion',
  'variant',
  'gameMode',
  'format',
  'placement',
  'eventType',
  'reason',
  'eventCount',
  'uniqueSessions',
  'uniquePlayers',
  'uniqueOpportunities',
  'firstSeenAt',
  'lastSeenAt',
  'avgElapsedMs',
  'p95ElapsedMs',
  'cohortDate',
  'cohortPlayers',
  'returnedDay1',
  'returnedDay7',
  'day1Mature',
  'day7Mature',
] as const;

const CSV_HEADERS = CSV_COLUMNS.map((column) => {
  if (column === 'uniquePlayers') return 'uniquePlayerIds';
  if (column === 'cohortPlayers') return 'cohortPlayerIds';
  return column;
});

function csvCell(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value);
  const safeText = typeof value === 'string' && /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safeText) ? `"${safeText.replaceAll('"', '""')}"` : safeText;
}

export function gameAdsReportToCsv(report: GameAdsAdminReport): string {
  const common = {
    windowDays: report.windowDays,
    generatedAt: report.generatedAt,
    collectionStartedAt: report.collectionStartedAt,
    evidence: report.evidence,
    identityBasis: report.identityBasis,
    identityCoverage: report.identityCoverage,
    missingIdentityEventsCount: report.missingIdentityEventsCount,
    retentionDayBoundary: report.retentionDayBoundary,
  };
  const eventRows = report.cohorts.map((row) => ({ ...common, recordType: 'event', ...row }));
  const retentionRows = report.retention.map((row) => ({
    ...common,
    recordType: 'retention',
    ...row,
  }));
  const rows = [...eventRows, ...retentionRows];
  return [
    CSV_HEADERS.join(','),
    ...rows.map((row) => {
      const values = row as unknown as Record<string, string | number | null | undefined>;
      return CSV_COLUMNS.map((column) => csvCell(values[column])).join(',');
    }),
  ].join('\r\n');
}
